/**
 * Latin → Cyrillic transliteration for Mongolian search queries (ADR-0009 §3,
 * open question #30).
 *
 * There is no standard romanisation of Mongolian: the same word arrives as
 * `uvul`, `ovol`, `övöl` or `uwul`, and the two letters that make Mongolian
 * Cyrillic differ from Russian — Ө and Ү — have no Latin letter of their own,
 * so users type `o`/`u` for both the plain and the front vowel. ICU's
 * `Cyrillic-Latin` transform assumes one spelling per letter and is wrong
 * exactly there. So this table is hand-built, forgiving, and produces SEVERAL
 * candidate spellings rather than one: the search layer ORs every candidate
 * into the query (`should` clauses over the Cyrillic title field) and lets the
 * index decide which one exists. We do not need to guess the right spelling;
 * we need the right spelling to be in the list.
 *
 * Why query-time and not index-time: an index-time `mn_latin` field would need
 * the SAME multiplicity — every product title stored under every Latin
 * spelling users might type — which is a combinatorial blow-up per document,
 * and the table could not change without a full reindex. Expanding the query
 * costs a few extra clauses per search and the table ships with the service.
 *
 * Rules, in order of precedence (longest Latin cluster wins):
 *   1. Digraphs that are ONE Cyrillic letter: kh→х, ch→ч, sh→ш, ts→ц, zh→ж,
 *      ya→я, yo→ё, yu→ю, ye→е. `ng` stays two letters (нг) but is matched as a
 *      unit so `n`+`g` never becomes н+ж via a later rule.
 *   2. Diphthongs: ai→ай, ei→эй, oi→ой/өй, ui→уй/үй, ii→ий/ы.
 *   3. Doubled vowels are long vowels: aa→аа, ee→ээ, oo→оо/өө, uu→уу/үү.
 *      A short-vowel alternative is kept at low rank because users also
 *      double a vowel to "emphasise" (`guutal` for гутал).
 *   4. Single letters, with the informal variants users actually type:
 *      u→у/ү/ө, o→о/ө, e→э/е, i→и/й, y→й/ы, c→ц/к, h→х, w→в, x→х, q→к,
 *      ö→ө, ü→ү, `'`→ь/ъ.
 *   5. Word-final `i` after a consonant may be the soft sign: mori→морь.
 *
 * Ranking: each alternative carries the rank of its position in the table
 * (0 = the most common spelling) and a candidate's score is the sum. On top
 * of that, VOWEL HARMONY — Mongolian words are either back-vowel (а о у) or
 * front-vowel (э ө ү), with и neutral — penalises a candidate that mixes the
 * two classes, so `хүүхэд` outranks `хуухэд` even though ү is the second
 * choice for `u`. Loanwords break harmony, which is why it is a penalty and
 * not a filter.
 *
 * This file is plain TypeScript with no dependencies: it is bundled into the
 * browser (the storefront shows "showing results for өвөл") as well as
 * running inside search-service.
 */
/** Ranked Cyrillic alternatives per Latin cluster; index in the array is the rank. */
const RULES = [
    // 1. one-letter digraphs
    ['kh', ['х']],
    ['ch', ['ч']],
    ['sh', ['ш']],
    ['ts', ['ц']],
    ['zh', ['ж']],
    ['ng', ['нг']],
    ['ya', ['я']],
    ['yo', ['ё', 'йо']],
    ['yu', ['ю']],
    ['ye', ['е', 'э']],
    // 2. diphthongs
    ['ai', ['ай']],
    ['ei', ['эй', 'ей']],
    ['oi', ['ой', 'өй']],
    ['ui', ['уй', 'үй']],
    ['ii', ['ий', 'ы']],
    // 3. long vowels (short alternative last, see rule 3)
    ['aa', ['аа', 'а']],
    ['ee', ['ээ', 'э']],
    ['oo', ['оо', 'өө', 'о']],
    ['uu', ['уу', 'үү', 'у']],
    ['öö', ['өө']],
    ['üü', ['үү']],
    // 4. single letters
    ['a', ['а']],
    ['b', ['б']],
    ['c', ['ц', 'к']],
    ['d', ['д']],
    ['e', ['э', 'е']],
    ['f', ['ф']],
    ['g', ['г']],
    ['h', ['х']],
    ['i', ['и', 'й']],
    ['j', ['ж']],
    ['k', ['к']],
    ['l', ['л']],
    ['m', ['м']],
    ['n', ['н']],
    ['o', ['о', 'ө']],
    ['p', ['п']],
    ['q', ['к']],
    ['r', ['р']],
    ['s', ['с']],
    ['t', ['т']],
    ['u', ['у', 'ү', 'ө']],
    ['v', ['в']],
    ['w', ['в']],
    ['x', ['х']],
    ['y', ['й', 'ы']],
    ['z', ['з']],
    ['ö', ['ө']],
    ['ü', ['ү']],
    ["'", ['ь', 'ъ']],
];
const RULE_MAP = new Map(RULES);
const MAX_CLUSTER = Math.max(...RULES.map(([latin]) => latin.length));
/** Rule 5: a final `i` after a consonant is often the soft sign (морь, хонь). */
const FINAL_I = ['и', 'ь', 'й'];
const BACK_VOWELS = new Set(['а', 'о', 'у', 'я', 'ё', 'ю']);
const FRONT_VOWELS = new Set(['э', 'ө', 'ү', 'е']);
const LATIN_VOWELS = new Set(['a', 'e', 'i', 'o', 'u', 'ö', 'ü', 'y']);
/** Score added to a candidate that mixes back and front vowels (see header). */
export const HARMONY_PENALTY = 2;
/** Candidates kept per word and per query; beyond this the tail is noise the query would pay for. */
export const MAX_CANDIDATES = 12;
function harmonyPenalty(word) {
    let back = false;
    let front = false;
    for (const ch of word) {
        if (BACK_VOWELS.has(ch))
            back = true;
        else if (FRONT_VOWELS.has(ch))
            front = true;
    }
    return back && front ? HARMONY_PENALTY : 0;
}
/** Split a Latin word into clusters, longest rule first. Unknown characters pass through unchanged. */
export function segment(word) {
    const out = [];
    let i = 0;
    while (i < word.length) {
        let matched = false;
        for (let len = Math.min(MAX_CLUSTER, word.length - i); len >= 1; len--) {
            const cluster = word.slice(i, i + len);
            if (RULE_MAP.has(cluster)) {
                out.push(cluster);
                i += len;
                matched = true;
                break;
            }
        }
        if (!matched) {
            out.push(word[i]);
            i += 1;
        }
    }
    return out;
}
function alternativesFor(cluster, isLast, previous) {
    if (cluster === 'i' && isLast && previous !== undefined && !LATIN_VOWELS.has(previous.at(-1))) {
        return FINAL_I;
    }
    return RULE_MAP.get(cluster) ?? [cluster];
}
/**
 * Every ranked spelling of one Latin word. A beam of `MAX_CANDIDATES` is kept
 * at each step so a long word cannot explode (u→3 × o→2 × … is exponential),
 * and the beam is ordered by score so what survives is the likely tail.
 */
export function transliterateWord(word) {
    const lower = word.toLowerCase();
    if (!lower)
        return [];
    const clusters = segment(lower);
    let beam = [{ text: '', score: 0 }];
    clusters.forEach((cluster, index) => {
        const alternatives = alternativesFor(cluster, index === clusters.length - 1, clusters[index - 1]);
        const next = [];
        for (const partial of beam) {
            alternatives.forEach((alt, rank) => {
                next.push({ text: partial.text + alt, score: partial.score + rank });
            });
        }
        // Harmony is judged on the partial word too, so a disharmonic prefix is
        // pruned early rather than surviving to the end on rank alone.
        next.sort((a, b) => a.score + harmonyPenalty(a.text) - (b.score + harmonyPenalty(b.text)));
        beam = next.slice(0, MAX_CANDIDATES);
    });
    return dedupe(beam.map((c) => ({ text: c.text, score: c.score + harmonyPenalty(c.text) }))).sort((a, b) => a.score - b.score || a.text.localeCompare(b.text));
}
function dedupe(candidates) {
    const best = new Map();
    for (const c of candidates) {
        const seen = best.get(c.text);
        if (!seen || c.score < seen.score)
            best.set(c.text, c);
    }
    return [...best.values()];
}
/**
 * Ranked Cyrillic spellings of a whole query. Words are transliterated
 * independently and recombined by total score, again under a beam, so
 * `khuukhdiin gutal` yields `хүүхдийн гутал` first and never more than
 * `MAX_CANDIDATES` strings. Whitespace is normalised to single spaces.
 */
export function transliterate(latin) {
    const words = latin.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0)
        return [];
    let beam = [{ text: '', score: 0 }];
    for (const word of words) {
        const perWord = transliterateWord(word);
        if (perWord.length === 0)
            continue;
        const next = [];
        for (const partial of beam) {
            for (const cand of perWord) {
                next.push({ text: partial.text ? `${partial.text} ${cand.text}` : cand.text, score: partial.score + cand.score });
            }
        }
        next.sort((a, b) => a.score - b.score || a.text.localeCompare(b.text));
        beam = next.slice(0, MAX_CANDIDATES);
    }
    return beam.map((c) => c.text);
}
const LATIN_QUERY = /^[a-zA-ZöüÖÜ'’\s-]+$/;
/**
 * Whether a query is worth transliterating: Latin letters only (with ö/ü and
 * the apostrophe used for ь), no digits, no Cyrillic.
 *
 * Deliberately permissive. A brand typed in Latin (`samsung`) is stored in
 * Cyrillic in many titles (Самсунг), so "is this English?" is the wrong
 * question — the candidates are `should` clauses that simply match nothing
 * when the query really was English. Digits switch it off because model
 * numbers (`iphone 15`) are never transliterated.
 */
export function looksLatinMongolian(query) {
    const trimmed = query.trim();
    return trimmed.length >= 2 && LATIN_QUERY.test(trimmed);
}
//# sourceMappingURL=transliteration.js.map